import { DateFilterOptions } from '../components/DateRangeFilter';

export interface DateRange {
  startDate: string;
  endDate: string;
}

// Construye Date local a medianoche del día dado
const fromLocalYMD = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Devuelve 'YYYY-MM-DD' en hora local
const toLocalYMD = (d: Date) => {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
};

// Inicio de día local
const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Diferencia de días calendario inclusiva (no utilizada actualmente)
// const diffDaysInclusive = (start: Date, end: Date) => {
//   const start0 = startOfLocalDay(start);
//   const end0   = startOfLocalDay(end);
//   const DAY_MS = 24 * 60 * 60 * 1000;
//   return Math.round((end0.getTime() - start0.getTime()) / DAY_MS) + 1;
// };

/**
 * Convierte las opciones de filtro de fecha a un rango de fechas efectivo
 */
export const getEffectiveDateRange = (options: DateFilterOptions): DateRange => {
  const today = startOfLocalDay(new Date());
  const todayStr = toLocalYMD(today);

  switch (options.mode) {
    case 'preset':
      switch (options.preset) {
        case 'hoy':
          return { startDate: todayStr, endDate: todayStr };
        case 'semana': {
          const oneWeekAgo = new Date(today);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 6);
          return { startDate: toLocalYMD(oneWeekAgo), endDate: todayStr };
        }
        case 'mes': {
          const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          return { startDate: toLocalYMD(firstDayOfMonth), endDate: todayStr };
        }
        case 'todos': {
          const threeMonthsAgo = new Date(today);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return { startDate: toLocalYMD(threeMonthsAgo), endDate: todayStr };
        }
        default:
          return { startDate: todayStr, endDate: todayStr };
      }
    case 'date': {
      const selected = options.date ? fromLocalYMD(options.date) : today;
      const ymd = toLocalYMD(selected);
      return { startDate: ymd, endDate: ymd };
    }
    case 'range': {
      const start = options.startDate ? fromLocalYMD(options.startDate) : today;
      const end   = options.endDate   ? fromLocalYMD(options.endDate)   : today;
      return { startDate: toLocalYMD(start), endDate: toLocalYMD(end) };
    }
    default:
      return { startDate: todayStr, endDate: todayStr };
  }
};

/**
 * Límites del rango como instantes ISO: inicio del primer día y fin del último, en hora
 * local. Así el servidor cuenta con el mismo día calendario que ve el usuario.
 */
export const toIsoBounds = (dateRange: DateRange): { fechaInicio: string; fechaFin: string } => {
  const start = fromLocalYMD(dateRange.startDate);
  const end = fromLocalYMD(dateRange.endDate);
  end.setHours(23, 59, 59, 999);
  return { fechaInicio: start.toISOString(), fechaFin: end.toISOString() };
};

/**
 * Filtra un array de registros de limpieza basado en el rango de fechas
 */
export const filterRegistrosByDateRange = <T extends { fechaHoraInicio: string }>(
  registros: T[],
  dateRange: DateRange
): T[] => {
  const start = startOfLocalDay(fromLocalYMD(dateRange.startDate));
  const end   = startOfLocalDay(fromLocalYMD(dateRange.endDate));
  end.setHours(23, 59, 59, 999);

  return registros.filter(registro => {
    try {
      const dt = new Date(registro.fechaHoraInicio);
      return dt >= start && dt <= end;
    } catch {
      return false;
    }
  });
};

/**
 * Obtiene un label descriptivo para mostrar el período seleccionado
 */
export const getDateRangeLabel = (options: DateFilterOptions): string => {
  if (options.mode === 'preset') {
    switch (options.preset) {
      case 'hoy': return 'Hoy';
      case 'semana': return 'Esta semana';
      case 'mes': return 'Este mes';
      case 'todos': return 'Todos los registros';
      default: return 'Período seleccionado';
    }
  } else if (options.mode === 'date') {
    const date = new Date((options.date || new Date().toISOString().split('T')[0]) + 'T12:00:00');
    return date.toLocaleDateString('es-ES');
  } else if (options.mode === 'range') {
    const startDate = new Date((options.startDate || new Date().toISOString().split('T')[0]) + 'T12:00:00');
    const endDate = new Date((options.endDate || new Date().toISOString().split('T')[0]) + 'T12:00:00');
    return `${startDate.toLocaleDateString('es-ES')} - ${endDate.toLocaleDateString('es-ES')}`;
  }
  
  return 'Período seleccionado';
};
